import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Viewer } from '../viewer/viewer';
import { DefaultColors } from '../defines';
import type { Subscription } from '@pointorama/potree-shared';
import { OpeningKind } from 'shared-types';
import { openingNameForKind } from './openingKindNames';
import {
  getAnnotationColorProperty,
  getAnnotationNameProperty,
  getAnnotationVisibleProperty,
  getOpeningSketchWallEdgeIdProperty,
  getOpeningSketchOffsetProperty,
  getOpeningSketchLengthProperty,
  getOpeningSketchLowerBoundProperty,
  getOpeningSketchHeightProperty,
  getOpeningSketchInsetProperty,
  getOpeningSketchKindProperty,
  getOpeningSketchDoorTypeProperty,
  getOpeningSketchHingeAtStartProperty,
  getOpeningSketchSwingPositiveProperty,
  getOpeningSketchRollableProperty,
  getOpeningSketchWindowTypeProperty,
} from '@pointorama/potree-shared';
import { getCustomOpeningType, onCustomOpeningTypesChanged } from '../sketchObjects/custom/openings/customOpeningRegistry';
import { specToParamSchema } from '../sketchObjects/custom/customSketchObjectType';
import { OPENING_PARAM_SCOPE, paramProperties } from '../sketchObjects/paramSchema';
import { customGlyphTypeOf } from './skeleton/openingGlyphs';
import { frameFromWallOpeningSketch, signedPerpFromInset } from './FloorMesh3DBuilder';
import { subSpan, tessellateOffset } from './skeleton/arcGeometry';
import type { PointoramaWallSketch } from './PointoramaWallSketch';

/**
 * Scene object for an OPENING_SKETCH (door/window on a wall-sketch edge).
 *
 * Rendering split: the door/window GLYPH (swing arc, leaf, sliding symbol) is
 * drawn by the owning {@link PointoramaWallSketch} as part of its skinning pass
 * (it already owns the glyph pipeline and the 3D wall cutouts). This object is a
 * lightweight, SELECTABLE marker: a thin line along the opening's span on the
 * wall centerline plus a fat transparent background line as the pick target.
 *
 * Geometry is parametric on the wall edge (offset/length/perp-offset along the
 * edge; lower/upper bound for the vertical extent), read from reactive
 * properties. When any geometry property changes, the line is rebuilt and the
 * wall sketch's glyphs are refreshed.
 */
export class PointoramaOpeningSketch extends THREE.Object3D {
  private _identifier = '';
  groupIdentifier: string | null = null;
  selected = false;
  finished = true;
  depthFadeEnabled = true;
  boundingSphere?: THREE.Sphere;

  private viewer: Viewer;
  private color: THREE.Color;

  private lineGeometry: LineGeometry;
  private line: Line2;
  public lineMaterial: LineMaterial;
  public backgroundLine: Line2;
  public backgroundLineMaterial: LineMaterial;

  private _colorSubscription?: Subscription;
  private _visibleSubscription?: Subscription;
  private _geometrySubscriptions: Subscription[] = [];
  /** Param properties of the organisation door / window this opening uses (re-bound when the type changes). */
  private _customParamSubscriptions: Subscription[] = [];
  private _customTypesUnsub: (() => void) | null = null;

  get identifier(): string {
    return this._identifier;
  }

  set identifier(value: string) {
    this._identifier = value;
    if (value) this.loadReactiveProperties();
  }

  constructor(viewer: Viewer, args: { color?: THREE.Color } = {}) {
    super();
    this.viewer = viewer;
    this.color = args.color ?? DefaultColors.OBJECTS_THREE;

    this.lineGeometry = new LineGeometry();
    this.lineMaterial = new LineMaterial({
      color: this.color,
      linewidth: 2,
      resolution: new THREE.Vector2(1000, 1000),
      depthTest: false,
      transparent: true,
    });
    this.line = new Line2(this.lineGeometry, this.lineMaterial);
    // The visible marker stripe is NOT drawn: the owning wall sketch's glyph
    // pass already renders the door/window symbol on the edge, so the colored
    // centerline stripe was pure duplication. The geometry stays (shared with
    // the fat invisible background line below — the pick/selection target).
    this.line.visible = false;
    this.add(this.line);

    this.backgroundLineMaterial = new LineMaterial({
      color: this.color,
      linewidth: 15,
      resolution: new THREE.Vector2(1000, 1000),
      transparent: true,
      depthTest: false,
      opacity: 0.0,
    });
    this.backgroundLine = new Line2(this.lineGeometry, this.backgroundLineMaterial);
    this.backgroundLine.name = 'OpeningSketchBackgroundLine';
    this.add(this.backgroundLine);
  }

  private loadReactiveProperties(): void {
    if (!this._identifier) return;
    try {
      this._colorSubscription?.unsubscribe();
      this._visibleSubscription?.unsubscribe();
      this._geometrySubscriptions.forEach((s) => s.unsubscribe());
      this._geometrySubscriptions = [];

      const colorProp = getAnnotationColorProperty(this._identifier);
      const visibleProp = getAnnotationVisibleProperty(this._identifier);

      if (colorProp.value) this.color = new THREE.Color(colorProp.value);
      this.visible = visibleProp.value;

      this._colorSubscription = colorProp.subscribeToChanges((value: string) => {
        if (value) {
          this.color = new THREE.Color(value);
          this.lineMaterial.color = this.color;
          this.backgroundLineMaterial.color = this.color;
        }
      });
      this._visibleSubscription = visibleProp.subscribeToChanges((value) => {
        this.visible = value;
      });

      // Geometry change → rebuild this marker line AND repaint the owning wall
      // sketch's glyphs (position/size of the symbol moved).
      const onGeometryChange = () => {
        this.rebuildLine();
        this.refreshOwningWallSketchGlyphs();
      };
      for (const prop of [
        getOpeningSketchWallEdgeIdProperty(this._identifier),
        getOpeningSketchOffsetProperty(this._identifier),
        getOpeningSketchLengthProperty(this._identifier),
        getOpeningSketchInsetProperty(this._identifier),
        // `swing_positive` picks which wall face the inset is measured from, so it
        // moves the glyph/marker off-centerline → treat as a geometry change.
        getOpeningSketchSwingPositiveProperty(this._identifier),
        getOpeningSketchLowerBoundProperty(this._identifier),
        getOpeningSketchHeightProperty(this._identifier),
      ]) {
        this._geometrySubscriptions.push(prop.subscribeToChanges(onGeometryChange));
      }

      // Glyph-only change (kind / door type / hinge / swing / rollable) → the
      // marker line is unchanged, so skip rebuildLine and just repaint the wall
      // sketch's glyph lines. This is the FAST path: rebuildOpeningGlyphsOnly
      // patches the glyph segments without re-running the wall-mesh CSG.
      const onGlyphChange = () => this.refreshOwningWallSketchGlyphs();
      const kindProp = getOpeningSketchKindProperty(this._identifier);
      // Kind (Door / Window) also drives the annotation name, mirroring the
      // floorplan OPENING (see OpeningAnnotation.applyKindToName).
      this.applyKindToName(kindProp.value);
      this._geometrySubscriptions.push(
        kindProp.subscribeToChanges((kind) => {
          this.applyKindToName(kind);
          onGlyphChange();
        }),
      );
      for (const prop of [getOpeningSketchHingeAtStartProperty(this._identifier), getOpeningSketchRollableProperty(this._identifier)]) {
        this._geometrySubscriptions.push(prop.subscribeToChanges(onGlyphChange));
      }
      // The symbol type (and with it which param properties matter) changes.
      const onTypeChange = () => {
        this.bindCustomParams(onGlyphChange);
        onGlyphChange();
      };
      this._geometrySubscriptions.push(getOpeningSketchDoorTypeProperty(this._identifier).subscribeToChanges(onTypeChange));
      this._geometrySubscriptions.push(getOpeningSketchWindowTypeProperty(this._identifier).subscribeToChanges(onTypeChange));
      this._geometrySubscriptions.push(kindProp.subscribeToChanges(() => this.bindCustomParams(onGlyphChange)));
      this._customTypesUnsub?.();
      // A library load can bring the type (or change its params); the wall sketch repaints itself.
      this._customTypesUnsub = onCustomOpeningTypesChanged(() => this.bindCustomParams(onGlyphChange));
      this.bindCustomParams(onGlyphChange);

      this.rebuildLine();
    } catch {
      // property service not ready yet
    }
  }

  /** Follow the param properties of the organisation door / window this opening currently uses. */
  private bindCustomParams(onChange: () => void): void {
    this._customParamSubscriptions.forEach((s) => s.unsubscribe());
    this._customParamSubscriptions = [];
    try {
      const typeId = customGlyphTypeOf({
        kind: getOpeningSketchKindProperty(this._identifier).value,
        doorType: getOpeningSketchDoorTypeProperty(this._identifier).value,
        windowType: getOpeningSketchWindowTypeProperty(this._identifier).value,
      });
      const type = typeId ? getCustomOpeningType(typeId) : null;
      if (!type) return;
      for (const prop of paramProperties(type.id, specToParamSchema(type.spec), this._identifier, OPENING_PARAM_SCOPE)) {
        this._customParamSubscriptions.push(prop.subscribeToChanges(onChange));
      }
    } catch {
      // property service not ready yet
    }
  }

  /** Update the annotation name to reflect the kind (Door / Window). */
  private applyKindToName(kind: OpeningKind): void {
    if (!this._identifier) return;
    try {
      const nameProperty = getAnnotationNameProperty(this._identifier);
      const next = openingNameForKind(nameProperty.value, kind, this.viewer.translationService);
      if (next !== nameProperty.value) nameProperty.value = next;
    } catch {
      // Global property service not initialized yet
    }
  }

  /** Find the wall sketch that owns this opening's edge, plus the edge endpoints
   *  and its face sizes (leftSize/rightSize, for the inset→perp conversion).
   *  Searched STRICTLY on this opening's own floor: pipeline edge ids are only
   *  unique per sketch (per-run counters), so a colliding id on another floor
   *  would resolve there and render the opening's marker at that floor's wall
   *  — leaking other floors' geometry into the selected floor's view. An
   *  opening whose own sketch lost the edge — or that has no floor at all —
   *  is an orphan and draws nothing (mirrors the wall sketch's strict
   *  glyph/cutter filters). */
  private resolveEdge(): { wallSketch: PointoramaWallSketch; start: THREE.Vector3; end: THREE.Vector3; leftSize: number; rightSize: number; bulge: number } | null {
    const edgeId = getOpeningSketchWallEdgeIdProperty(this._identifier).value || '';
    if (!edgeId) return null;
    const candidates = this.viewer.scene.wallSketches.filter((s) => s.groupIdentifier === this.groupIdentifier);
    for (const ws of candidates) {
      const edge = ws.getEdge(edgeId);
      if (!edge) continue;
      const a = ws.getNode(edge.startNodeId);
      const b = ws.getNode(edge.endNodeId);
      if (!a || !b) continue;
      return {
        wallSketch: ws,
        start: new THREE.Vector3(a.position.x, a.position.y, a.position.z),
        end: new THREE.Vector3(b.position.x, b.position.y, b.position.z),
        leftSize: edge.leftSize ?? 0.1,
        rightSize: edge.rightSize ?? 0.1,
        bulge: edge.bulge ?? 0,
      };
    }
    return null;
  }

  /** Signed centerline perpOffset for this opening, derived from its stored
   *  `inset` (from the outer face) + `swing_positive` + the resolved edge sizes. */
  private computePerpOffset(resolved: { leftSize: number; rightSize: number }): number {
    const inset = getOpeningSketchInsetProperty(this._identifier).value ?? 0;
    const swingPositive = getOpeningSketchSwingPositiveProperty(this._identifier).value;
    return signedPerpFromInset(inset, swingPositive, resolved.leftSize, resolved.rightSize);
  }

  private rebuildLine(): void {
    const resolved = this.resolveEdge();
    if (!resolved) {
      this.lineGeometry.setPositions([0, 0, 0, 0, 0, 0]);
      return;
    }
    const offset = getOpeningSketchOffsetProperty(this._identifier).value ?? 0;
    const length = getOpeningSketchLengthProperty(this._identifier).value ?? 0;
    const perpOffset = this.computePerpOffset(resolved);
    const lower = getOpeningSketchLowerBoundProperty(this._identifier).value ?? 0;
    // `height` is stored relative to the ground offset; head Z = lower + height.
    const upper = lower + (getOpeningSketchHeightProperty(this._identifier).value ?? 0);

    const frame = frameFromWallOpeningSketch(
      { x: resolved.start.x, y: resolved.start.y },
      { x: resolved.end.x, y: resolved.end.y },
      { offset, length, perpOffset },
      resolved.bulge,
    );
    if (!frame) {
      this.lineGeometry.setPositions([0, 0, 0, 0, 0, 0]);
      return;
    }
    // Draw the marker at the vertical mid-height so it's visible in 3D and on top
    // in 2D-top views. The marker follows the wall: a straight segment on a
    // straight wall, a tessellated arc span (offset by perpOffset) on a curved one.
    const z = (lower + upper) / 2;
    const span = subSpan({ x: resolved.start.x, y: resolved.start.y }, { x: resolved.end.x, y: resolved.end.y }, resolved.bulge, offset, offset + length);
    const pts = tessellateOffset(span.start, span.end, span.bulge, perpOffset);
    const positions: number[] = [];
    for (const p of pts) positions.push(p.x, p.y, z);
    this.lineGeometry.setPositions(positions);
    this.computeLineDistances();
  }

  private computeLineDistances(): void {
    this.line.computeLineDistances();
    this.backgroundLine.computeLineDistances();
  }

  /**
   * World-space bounding box of this opening's full extent: its span along the
   * wall (length), the wall thickness (left+right size) it sits in, and its
   * vertical extent (lower→upper bound). Used to focus/frame the camera on it.
   * Returns null when the referenced wall edge can't be resolved.
   */
  getBoundingBox(): THREE.Box3 | null {
    const resolved = this.resolveEdge();
    if (!resolved) return null;

    const edgeId = getOpeningSketchWallEdgeIdProperty(this._identifier).value || '';
    const edge = resolved.wallSketch.getEdge(edgeId);
    const offset = getOpeningSketchOffsetProperty(this._identifier).value ?? 0;
    const length = getOpeningSketchLengthProperty(this._identifier).value ?? 0;
    const perpOffset = this.computePerpOffset(resolved);
    const lower = getOpeningSketchLowerBoundProperty(this._identifier).value ?? 0;
    // `height` is stored relative to the ground offset; head Z = lower + height.
    const upper = lower + (getOpeningSketchHeightProperty(this._identifier).value ?? 0);

    const frame = frameFromWallOpeningSketch({ x: resolved.start.x, y: resolved.start.y }, { x: resolved.end.x, y: resolved.end.y }, { offset, length, perpOffset }, resolved.bulge);
    if (!frame) return null;

    const leftSize = edge?.leftSize ?? 0.1;
    const rightSize = edge?.rightSize ?? 0.1;
    const box = new THREE.Box3();
    // Four corners of the opening's along-wall span, on both faces of the wall
    // band, at the bottom and top of the vertical extent.
    for (const u of [-frame.halfWidth, frame.halfWidth]) {
      for (const n of [-rightSize, leftSize]) {
        for (const z of [lower, upper]) {
          box.expandByPoint(new THREE.Vector3(frame.center.x + u * frame.wallDir.x + n * frame.wallNorm.x, frame.center.y + u * frame.wallDir.y + n * frame.wallNorm.y, z));
        }
      }
    }
    return box.isEmpty() ? null : box;
  }

  private refreshOwningWallSketchGlyphs(): void {
    const resolved = this.resolveEdge();
    resolved?.wallSketch.rebuildOpeningGlyphsOnly();
  }

  /**
   * Select: the owning wall sketch draws this opening's glyph in the selection
   * color (it reads `os.selected` when building glyph segments), so flag it and
   * repaint the glyphs. No-op if already selected.
   */
  select(): void {
    if (this.selected) return;
    this.selected = true;
    this.refreshOwningWallSketchGlyphs();
  }

  deselect(): void {
    if (!this.selected) return;
    this.selected = false;
    this.refreshOwningWallSketchGlyphs();
  }

  dispose(): void {
    this._colorSubscription?.unsubscribe();
    this._visibleSubscription?.unsubscribe();
    this._geometrySubscriptions.forEach((s) => s.unsubscribe());
    this._geometrySubscriptions = [];
    this._customParamSubscriptions.forEach((s) => s.unsubscribe());
    this._customParamSubscriptions = [];
    this._customTypesUnsub?.();
    this._customTypesUnsub = null;
    this.lineGeometry.dispose();
    this.lineMaterial.dispose();
    this.backgroundLineMaterial.dispose();
  }
}
